import { useEffect, useState } from "react";
import { useSettingsCache } from "@/shared/cache";
import type {
	EmailGroupingMode,
	Settings,
} from "@/shared/cache/slices/settings.types";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { NumberStepper } from "@/v2/components/settings/NumberStepper";
import { SectionHeading } from "@/v2/components/settings/SectionHeading";
import { SettingRow } from "@/v2/components/settings/SettingRow";
import { Button } from "@/v2/components/ui/button";
import { Card, CardContent } from "@/v2/components/ui/card";
import { Switch } from "@/v2/components/ui/switch";
import { Textarea } from "@/v2/components/ui/textarea";

const PROMPT_FIELD_MAX_CHARS = 2000;

function TrackInternalEmailsToggle({
	trackInternal,
	update,
}: {
	trackInternal: boolean;
	update: (patch: Partial<Settings>) => void;
}) {
	return (
		<SettingRow
			title="Track internal emails"
			description="Track emails where all participants are within your organization"
		>
			<Switch
				checked={trackInternal}
				onCheckedChange={(v) =>
					// Force include=false when track goes off — the dependent
					// narrative-scrubbing setting (managed on the Narratives page)
					// is meaningless without tracking, so we clear it here too to
					// avoid a stale `true` lingering in the cache.
					update(
						v
							? { p_track_internal_emails: true }
							: {
									p_track_internal_emails: false,
									p_include_internal_in_narratives: false,
								},
					)
				}
			/>
		</SettingRow>
	);
}

function EmailGroupingToggles({
	mode,
	onChange,
}: {
	mode: EmailGroupingMode;
	onChange: (mode: EmailGroupingMode) => void;
}) {
	const groupDrafts = mode === "combined" || mode === "grouped" || mode === "individual_review";
	const groupReviews = mode === "combined" || mode === "grouped" || mode === "individual_draft";
	const combineAll = mode === "combined";

	const resolveMode = (
		drafts: boolean,
		reviews: boolean,
		combine: boolean,
	): EmailGroupingMode => {
		if (combine) return "combined";
		if (drafts && reviews) return "grouped";
		if (!drafts && reviews) return "individual_draft";
		if (drafts && !reviews) return "individual_review";
		return "individual";
	};

	return (
		<>
			<SettingRow
				title="One entry per thread"
				description="All email activity in a thread — drafting and reviewing — is merged into a single time entry."
			>
				<Switch
					checked={combineAll}
					onCheckedChange={(v) =>
						onChange(v ? "combined" : resolveMode(groupDrafts, groupReviews, false))
					}
				/>
			</SettingRow>
			{!combineAll && (
				<div className="pl-6">
					<SettingRow
						title="One entry per thread for drafts"
						description="Composing multiple emails in the same thread produces one time entry instead of one per email."
					>
						<Switch
							checked={groupDrafts}
							onCheckedChange={(v) =>
								onChange(resolveMode(v, groupReviews, false))
							}
						/>
					</SettingRow>
					<SettingRow
						title="One entry per thread for reviews"
						description="Reading multiple emails in the same thread produces one time entry instead of one per email."
					>
						<Switch
							checked={groupReviews}
							onCheckedChange={(v) =>
								onChange(resolveMode(groupDrafts, v, false))
							}
						/>
					</SettingRow>
				</div>
			)}
		</>
	);
}

function EmailFilteringPromptBlock({
	settings,
	updateSettings,
}: {
	settings: Settings;
	updateSettings: (patch: Partial<Settings>) => unknown;
}) {
	const current = settings.p_email_filtering_prompt;
	const [draft, setDraft] = useState(current);

	useEffect(() => {
		setDraft(current);
	}, [current]);

	const isDirty = draft !== current;
	const overLimit = draft.length > PROMPT_FIELD_MAX_CHARS;

	return (
		<div className="flex flex-col gap-3 px-2 py-4 border-b border-border/40">
			<div>
				<p className="text-sm font-medium">Email filtering instructions</p>
				<p className="text-sm text-muted-foreground">
					Extra guidance for deciding whether emails without a matched matter
					should be tracked.
				</p>
			</div>
			<Textarea
				value={draft}
				onChange={(e) =>
					setDraft(e.target.value.slice(0, PROMPT_FIELD_MAX_CHARS))
				}
				maxLength={PROMPT_FIELD_MAX_CHARS}
				placeholder='e.g. Ignore emails about conflict checks.'
				className="min-h-[140px]"
			/>
			<div className="flex items-center justify-between">
				<span
					className={`text-xs ${
						overLimit ? "text-destructive" : "text-muted-foreground"
					}`}
				>
					{draft.length} / {PROMPT_FIELD_MAX_CHARS}
				</span>
				{isDirty && (
					<div className="flex gap-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() => setDraft(current)}
						>
							Cancel
						</Button>
						<Button
							size="sm"
							disabled={overLimit}
							onClick={() => updateSettings({ p_email_filtering_prompt: draft })}
						>
							Save
						</Button>
					</div>
				)}
			</div>
		</div>
	);
}

export default function Tracking() {
	useDocumentTitle("Tracking");
	const { settings, updateSettings } = useSettingsCache();

	return (
		<div>
			<h2 className="text-lg font-semibold mb-4">Tracking</h2>

			<SectionHeading>General</SectionHeading>
			<SettingRow
				title="Track entries after export"
				description="Entries will continue to be tracked even after they are exported"
			>
				<Switch
					checked={settings.track_after_exported}
					onCheckedChange={(v) =>
						updateSettings({ track_after_exported: v })
					}
				/>
			</SettingRow>
			<SettingRow
				title="Auto-populate last used matter"
				description="When adding an entry, pre-fill the matter from your most recent entry."
			>
				<Switch
					checked={settings.auto_populate_last_used_matter}
					onCheckedChange={(v) =>
						updateSettings({ auto_populate_last_used_matter: v })
					}
				/>
			</SettingRow>
			<SettingRow
				title="Combine actual time"
				description="Bill combined entries from their total tracked time, rounded once. When off, each entry is rounded up to the billing increment before the entries are summed."
			>
				<Switch
					checked={settings.p_combine_actual_units}
					onCheckedChange={(v) =>
						updateSettings({ p_combine_actual_units: v })
					}
				/>
			</SettingRow>
			<SectionHeading>Emails</SectionHeading>
			<SettingRow
				title="Tracking threshold (seconds)"
				description="How long you must spend on an email before it starts being tracked"
			>
				<NumberStepper
					value={settings.time_to_track_emails}
					onChange={(v) => updateSettings({ time_to_track_emails: v })}
					min={5}
					step={5}
				/>
			</SettingRow>
			<TrackInternalEmailsToggle
				trackInternal={settings.p_track_internal_emails}
				update={updateSettings}
			/>
			<EmailGroupingToggles
				mode={settings.email_grouping_mode}
				onChange={(mode) => updateSettings({ email_grouping_mode: mode })}
			/>
			<EmailFilteringPromptBlock
				settings={settings}
				updateSettings={updateSettings}
			/>

			<SectionHeading>Documents</SectionHeading>
			<SettingRow
				title="Tracking threshold (seconds)"
				description="How long you must spend on a document before it starts being tracked"
			>
				<NumberStepper
					value={settings.time_to_track_docs}
					onChange={(v) => updateSettings({ time_to_track_docs: v })}
					min={5}
					step={5}
				/>
			</SettingRow>
			<SectionHeading>Browser</SectionHeading>
			<SettingRow
				title="Tracking threshold (seconds)"
				description="How long you must spend on a page before it starts being tracked"
			>
				<NumberStepper
					value={settings.p_time_to_track_on_browser_s}
					onChange={(v) => updateSettings({ p_time_to_track_on_browser_s: v })}
					min={5}
					step={5}
				/>
			</SettingRow>
			<SettingRow
				title="Session inactivity timeout (minutes)"
				description="How long you must be inactive before browsing is tracked as a new entry"
			>
				<NumberStepper
					value={settings.p_browser_session_inactivity_threshold_m}
					onChange={(v) => updateSettings({ p_browser_session_inactivity_threshold_m: v })}
					min={5}
					step={5}
				/>
			</SettingRow>

			<SectionHeading>Meetings</SectionHeading>
			<SettingRow
				title="Time to track meetings (seconds)"
				description="How long you must spend in a meeting before it starts being tracked"
			>
				<NumberStepper
					value={settings.p_time_to_track_meetings_s}
					onChange={(v) => updateSettings({ p_time_to_track_meetings_s: v })}
					min={5}
					step={5}
				/>
			</SettingRow>

			<Card className="mt-8 border-destructive/40 bg-destructive/10 dark:bg-destructive/10">
				<CardContent className="p-0">
					<h3 className="border-b border-destructive/20 px-4 py-3 text-base font-medium text-destructive">
						Danger Zone
					</h3>
					<div className="flex items-center justify-between gap-4 px-4 py-4">
						<div className="min-w-0 flex-1">
							<p className="text-sm font-medium">Automatic tracking</p>
							<p className="text-sm text-muted-foreground">
								Turning this off will make it so entries are no longer
								automatically generated from your activity.
							</p>
						</div>
						<div className="shrink-0">
							<Switch
								checked={settings.p_auto_tracking}
								onCheckedChange={(v) =>
									updateSettings({ p_auto_tracking: v })
								}
							/>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
